import React, {useState, useEffect} from 'react';
import {
    X,
    Package,
    Tag,
    Clock,
    Calendar,
    DollarSign,
    ShoppingCart,
    Archive,
    Box,
    Barcode,
    ChevronDown,
    ChevronUp,
    Save,
    Printer
} from 'lucide-react';
import {api} from '../../../config/api';
import secureStorage from '../../../utils/secureStorage';
import LabelPrintModal from '../../Labels/LabelPrintModal';
import PurchasePricesSection from './PurchasePricesSection';
import Notification from "../../Common/Notification";

const ProductDetailsModal = ({productId, isOpen, onClose, onUpdate}) => {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showOnCalendar, setShowOnCalendar] = useState(false);
    const [duration, setDuration] = useState(30);
    const [notification, setNotification] = useState({show: false, type: 'success', message: ''});
    const [showCalendarSettings, setShowCalendarSettings] = useState(true);
    const [showLabelModal, setShowLabelModal] = useState(false);

    useEffect(() => {
        if (isOpen && productId) {
            fetchProductDetails();
        }
    }, [isOpen, productId]);

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const accountId = await secureStorage.getItem('selectedAccount');
            const response = await api.get(`/products/${productId}`, {
                headers: {'X-account-id': accountId.id}
            });
            const productData = response.data.data || response.data;
            setProduct(productData);
            setShowOnCalendar(productData.showOnCalendar || false);
            setDuration(productData.duration || 30);
        } catch (error) {
            console.error('Error loading product details:', error);
            showNotificationMessage('error', 'Erreur lors du chargement des détails');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateCalendarSettings = async () => {
        try {
            const accountId = await secureStorage.getItem('selectedAccount');
            const formData = new FormData();
            formData.append('show_on_calendar', showOnCalendar);
            formData.append('duration', duration);
            await api.put(`/products/${productId}`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': 'multipart/form-data'
                }
            });
            showNotificationMessage('success', 'Paramètres calendrier mis à jour');
            onUpdate?.();
        } catch (error) {
            console.error('Error updating calendar settings:', error);
            showNotificationMessage('error', 'Erreur lors de la mise à jour');
        }
    };

    const showNotificationMessage = (type, message) => {
        setNotification({show: true, type, message});
        if (type === 'success') {
            setTimeout(() => setNotification(p => ({...p, show: false})), 3000);
        }
    };

    if (!isOpen) return null;

    return (
        /* ── Scrollable overlay ── */
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop — click to close */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Positioning wrapper: bottom-sheet on mobile, centred on sm+ */}
            <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                {/* Modal panel */}
                <div
                    className="relative bg-white dark:bg-gray-900 w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl overflow-hidden"
                    style={{boxShadow: '0 25px 60px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.05)'}}
                    onClick={e => e.stopPropagation()}
                >
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-900 px-4 sm:px-5 py-3 sm:py-4 flex justify-between items-center z-10 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Package className="w-4 h-4 text-primary"/>
                        </div>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Détails du produit</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {product && (
                            <button
                                onClick={() => setShowLabelModal(true)}
                                title="Imprimer une étiquette"
                                className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors"
                            >
                                <Printer className="w-4 h-4"/>
                                <span className="hidden sm:inline">Étiquette</span>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <X className="w-4 h-4"/>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-100 border-t-primary"></div>
                        <p className="text-xs text-gray-400">Chargement...</p>
                    </div>
                ) : product ? (
                    <div className="p-4 sm:p-5 space-y-4">

                        {/* Top: image + main info — stacked on mobile, side-by-side on sm+ */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* Image / color block */}
                            <div
                                className="w-full sm:w-28 sm:h-28 h-40 rounded-xl overflow-hidden flex-shrink-0"
                                style={{boxShadow: '0 4px 14px rgba(0,0,0,0.12)'}}>
                                {product.imageUrl && product.imageUrl !== '' ? (
                                    <img
                                        src={product.imageUrl}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="w-full h-full flex items-center justify-center"
                                        style={{backgroundColor: product.color && product.color !== '' ? product.color : '#4DB6AC'}}
                                    >
                                        <Package className="w-10 h-10 text-white opacity-60"/>
                                    </div>
                                )}
                            </div>

                            {/* Name + badges + price */}
                            <div className="flex-1 min-w-0 space-y-2">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-snug">{product.name}</h3>
                                    {product.description && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2">{product.description}</p>
                                    )}
                                </div>

                                {/* Badges */}
                                <div className="flex flex-wrap gap-1.5">
                                    {product.category && (
                                        <span
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium"
                                            style={{
                                                backgroundColor: product.category.color ? `${product.category.color}20` : '#e5e7eb',
                                                color: product.category.color || '#374151',
                                            }}>
                                            <Tag className="w-2.5 h-2.5"/>
                                            {product.category.name}
                                        </span>
                                    )}
                                    {product.brand && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
                                            <Package className="w-2.5 h-2.5"/>
                                            {product.brand.name}
                                        </span>
                                    )}
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                                        product.active
                                            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                                            : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                                    }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${product.active ? 'bg-emerald-500' : 'bg-red-400'}`}></span>
                                        {product.active ? 'Actif' : 'Inactif'}
                                    </span>
                                </div>

                                {/* Price + Stock inline */}
                                <div className="flex gap-3">
                                    <div
                                        className="flex-1 rounded-xl px-3 py-2 bg-blue-50 dark:bg-blue-900/15"
                                        style={{boxShadow: 'inset 0 1px 3px rgba(59,130,246,0.1)'}}>
                                        <div className="text-xs text-blue-500 dark:text-blue-400 font-medium mb-0.5">Prix</div>
                                        <div className="text-lg font-bold text-blue-700 dark:text-blue-300">
                                            {product.price ? Number(product.price).toFixed(2) : '—'}
                                            {product.price && <span className="text-xs font-normal ml-0.5 text-blue-500"> DH</span>}
                                        </div>
                                    </div>
                                    {product.isStockable && (
                                        <div
                                            className="flex-1 rounded-xl px-3 py-2 bg-emerald-50 dark:bg-emerald-900/15"
                                            style={{boxShadow: 'inset 0 1px 3px rgba(16,185,129,0.1)'}}>
                                            <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-0.5">Stock</div>
                                            <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                                                {product.stock || 0}
                                                {product.units?.symbol && <span className="text-xs font-normal ml-0.5 text-emerald-500"> {product.units.symbol}</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-gray-100 dark:border-gray-800"/>

                        {/* Infos détaillées */}
                        <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Informations</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {[
                                    product.barcode && {
                                        icon: <Barcode className="w-3 h-3"/>,
                                        label: 'Code-barres',
                                        value: <span className="font-mono">{product.barcode}</span>
                                    },
                                    {
                                        label: 'Unité',
                                        value: product.units?.name ? `${product.units.name}${product.units.symbol ? ` (${product.units.symbol})` : ''}` : 'N/A'
                                    },
                                    {
                                        label: 'Taxe',
                                        value: product.taxes ? `${product.taxes.name} · ${product.taxes.value}%` : 'N/A'
                                    },
                                    {
                                        icon: <ShoppingCart className="w-3 h-3"/>,
                                        label: 'Vendable',
                                        value: product.isVendable
                                            ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Oui</span>
                                            : <span className="text-gray-400">Non</span>
                                    },
                                    {
                                        icon: <Archive className="w-3 h-3"/>,
                                        label: 'Stockable',
                                        value: product.isStockable
                                            ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Oui</span>
                                            : <span className="text-gray-400">Non</span>
                                    },
                                    product.cost && {
                                        label: 'Coût',
                                        value: `${Number(product.cost).toFixed(2)} DH`
                                    },
                                    product.isIngredient && {
                                        icon: <Archive className="w-3 h-3"/>,
                                        label: 'Ingrédient',
                                        value: <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Oui</span>
                                    },
                                    product.internalCode && {
                                        label: 'Code interne',
                                        value: product.internalCode
                                    },
                                    product.purchasePriceHt != null && {
                                        label: "Prix d'achat",
                                        value: `${Number(product.purchasePriceHt).toFixed(2)} DH HT${product.purchasePriceTtc != null ? ` / ${Number(product.purchasePriceTtc).toFixed(2)} DH TTC` : ''}`
                                    }
                                ].filter(Boolean).map((item, i) => (
                                    <div key={i} className="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2.5">
                                        <div className="flex items-center gap-1 text-[10px] text-gray-400 uppercase tracking-wide mb-1">
                                            {item.icon}
                                            {item.label}
                                        </div>
                                        <div className="text-sm text-gray-800 dark:text-gray-200">{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Calendrier */}
                        <div className="rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-800/60"
                             style={{boxShadow: '0 1px 3px rgba(0,0,0,0.06)'}}>
                            <button
                                onClick={() => setShowCalendarSettings(!showCalendarSettings)}
                                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700/40 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                                        <Calendar className="w-3.5 h-3.5 text-primary"/>
                                    </div>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Paramètres calendrier</span>
                                </div>
                                {showCalendarSettings
                                    ? <ChevronUp className="w-4 h-4 text-gray-400"/>
                                    : <ChevronDown className="w-4 h-4 text-gray-400"/>}
                            </button>

                            {showCalendarSettings && (
                                <div className="px-4 pb-4 pt-3 space-y-3 border-t border-gray-200 dark:border-gray-700">
                                    <label className="flex items-center gap-2.5 cursor-pointer group w-fit">
                                        <input
                                            type="checkbox"
                                            checked={showOnCalendar}
                                            onChange={(e) => setShowOnCalendar(e.target.checked)}
                                            className="w-4 h-4 rounded text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                                            Afficher dans le calendrier
                                        </span>
                                    </label>

                                    {showOnCalendar && (
                                        <div className="ml-6 flex items-center gap-2 pl-3 border-l-2 border-primary/30">
                                            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                            <input
                                                type="number"
                                                value={duration}
                                                onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                                                min="5"
                                                step="5"
                                                className="w-20 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                            />
                                            <span className="text-sm text-gray-400">minutes</span>
                                        </div>
                                    )}

                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleUpdateCalendarSettings}
                                            className="inline-flex items-center gap-1.5 bg-primary text-white px-4 py-1.5 rounded-lg hover:bg-primary/90 active:scale-95 transition-all text-sm font-medium"
                                            style={{boxShadow: '0 2px 8px rgba(77,182,172,0.35)'}}>
                                            <Save className="w-3.5 h-3.5"/>
                                            Enregistrer
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Prix d'achat (produits stockables) */}
                        {product.isStockable && (
                            <PurchasePricesSection productId={productId} productName={product.name}/>
                        )}

                        {/* Variantes */}
                        {product.variants && product.variants.length > 0 && (
                            <div>
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                                    Variantes
                                    <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-[10px]">
                                        {product.variants.length}
                                    </span>
                                </p>
                                <div className="space-y-2">
                                    {product.variants.map((variant) => (
                                        <div key={variant.id}
                                             className="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                             style={{boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}}>
                                            <div className="flex justify-between items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{variant.name}</div>
                                                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                                                        <span className="flex items-center gap-0.5">
                                                            <DollarSign className="w-3 h-3"/>
                                                            {variant.price ? `${Number(variant.price).toFixed(2)} DH` : '—'}
                                                        </span>
                                                        {variant.isStockable && (
                                                            <span className="flex items-center gap-0.5">
                                                                <Box className="w-3 h-3"/>
                                                                {variant.stock || 0}
                                                            </span>
                                                        )}
                                                        {variant.barcode && (
                                                            <span className="flex items-center gap-0.5 font-mono">
                                                                <Barcode className="w-3 h-3"/>
                                                                {variant.barcode}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium flex-shrink-0 ${
                                                    variant.active
                                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                                                        : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                                                }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${variant.active ? 'bg-emerald-500' : 'bg-red-400'}`}></span>
                                                    {variant.active ? 'Actif' : 'Inactif'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notification */}
                        <Notification
                            show={notification.show}
                            type={notification.type}
                            message={notification.message}
                            onClose={() => setNotification(prev => ({...prev, show: false}))}
                        />
                        {/* Safe-area bottom spacing on mobile */}
                        <div className="h-safe-area-bottom sm:hidden pb-2"/>
                    </div>
                ) : null}
                </div>{/* end modal panel */}
            </div>{/* end positioning wrapper */}

            {/* Label Print Modal */}
            <LabelPrintModal
                open={showLabelModal && !!product}
                type="product"
                items={product ? [product] : []}
                onClose={() => setShowLabelModal(false)}
            />
        </div>
    );
};

export default ProductDetailsModal;
